import { BANNER_KEYS, BANNER_TYPES, BANNER_VARIANTS, BUTTON_STYLE, BUTTON_TYPE, REGEX_PATTERNS, WORKSPACE_ENVIRONMENT } from '@/app.constants';
import Banner from '@/components/ui/banner/Banner';
import Button from '@/components/ui/button/index.tsx';
import InputField from '@/components/ui/inputfield';
import Select from '@/components/ui/select';
import SideSheet from '@/components/ui/side-sheet/side-sheet';
import SidesheetFooter from '@/components/ui/sidesheet-footer';
import toast from '@/config/react-toast';
import { CustomEnvOverride, CustomHeader, getCustomBaseUrl, getCustomEnvOverrides, getCustomHeaders, removeCustomBaseUrl, removeCustomEnvOverrides, removeCustomHeaders, setCustomBaseUrl, setCustomEnvOverrides, setCustomHeaders } from '@/utils/developer-settings';
import { OVERRIDABLE_ENV_KEYS } from '@/utils/get-environment-value';
import { Code, Link, Plus, Server, Trash2, Wifi, Wrench } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import PerfectScrollbar from 'react-perfect-scrollbar';

interface FormData {
  customBaseUrl: string;
  customHeaders: CustomHeader[];
  customEnvOverrides: CustomEnvOverride[];
}

const DEFAULT_OCEANINFRA_GATEWAY_URL = 'http://api-gateway.oceaninfra.localhost/';
const ENV_OVERRIDE_LABELS: Record<string, string> = {
  VITE_PUSHER_APP_CLUSTER: 'Pusher App Cluster',
  VITE_PUSHER_CHANNEL_NAME_PREFIX: 'Pusher Channel Prefix',
  VITE_PUSHER_APP_KEY: 'Pusher App Key'
};
type EnvVariableOption = { label: string; value: string };

const normalizeEntries = <T extends { key: string; value: string }>(entries: T[]) => {
  return (entries || [])
    .map(({ key, value }) => ({ key: key?.trim(), value: value?.trim() }))
    .filter(({ key, value }) => key && value)
    .sort((a, b) => `${a.key}:${a.value}`.localeCompare(`${b.key}:${b.value}`));
};

const DeveloperSettingsModalContent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const customHeaders = getCustomHeaders();
  const customBaseUrl = getCustomBaseUrl();
  const customEnvOverrides = getCustomEnvOverrides();
  const areCustomSettingsPresent = customHeaders.length > 0 || !!customBaseUrl || customEnvOverrides.length > 0;
  const AVAILABLE_ENV_VARIABLE_OPTIONS: EnvVariableOption[] = OVERRIDABLE_ENV_KEYS.map((key) => ({
    label: ENV_OVERRIDE_LABELS[key] || key,
    value: key
  }));

  const form = useForm<FormData>({
    defaultValues: {
      customBaseUrl: '',
      customHeaders: [{ key: '', value: '' }],
      customEnvOverrides: [{ key: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'customHeaders',
  });
  const {
    fields: envOverrideFields,
    append: appendEnvOverride,
    remove: removeEnvOverride
  } = useFieldArray({
    control: form.control,
    name: 'customEnvOverrides',
  });
  const allHeaderFields = form.watch('customHeaders');
  const allEnvOverrideFields = form.watch('customEnvOverrides');

  const handleOpenModal = () => {
    setIsOpen(true);
    form.reset({
      customBaseUrl: customBaseUrl,
      customHeaders: customHeaders.length > 0 ? customHeaders : [{ key: '', value: '' }],
      customEnvOverrides: customEnvOverrides.length > 0 ? customEnvOverrides : [{ key: '', value: '' }],
    });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const onSubmit = (data: FormData) => {
    const validHeaders = data.customHeaders.filter((header) => header.key.trim() && header.value.trim());
    const validEnvOverrides = data.customEnvOverrides.filter((override) => override.key.trim() && override.value.trim());
    const newBaseUrl = data.customBaseUrl.trim().replace(/\/$/, '');
    const hasBaseUrlChanged = newBaseUrl !== (customBaseUrl || '');
    const hasHeadersChanged = JSON.stringify(normalizeEntries(validHeaders)) !== JSON.stringify(normalizeEntries(customHeaders));
    const hasEnvOverridesChanged = JSON.stringify(normalizeEntries(validEnvOverrides)) !== JSON.stringify(normalizeEntries(customEnvOverrides));
    const hasAnyDevSettingChanged = hasBaseUrlChanged || hasHeadersChanged || hasEnvOverridesChanged;

    if (!hasAnyDevSettingChanged) {
      toast('No changes to apply', { type: 'success' });
      setIsOpen(false);
      return;
    }

    if (hasBaseUrlChanged && (customBaseUrl || newBaseUrl)) {
      // If Base URL changed, clear everything but then restore custom settings
      localStorage.clear();
      setCustomHeaders(validHeaders);
      setCustomBaseUrl(newBaseUrl);
      setCustomEnvOverrides(validEnvOverrides, [...OVERRIDABLE_ENV_KEYS]);
      toast('Base URL changed. Session cleared.', { type: 'success' });
    } else {
      setCustomHeaders(validHeaders);
      setCustomBaseUrl(newBaseUrl);
      setCustomEnvOverrides(validEnvOverrides, [...OVERRIDABLE_ENV_KEYS]);
      
      const wasAlreadyEmpty = !customHeaders.length && !customBaseUrl && !customEnvOverrides.length;
      const isNowEmpty = !validHeaders.length && !newBaseUrl && !validEnvOverrides.length;

      if (isNowEmpty) {
        if (!wasAlreadyEmpty) {
          toast('All custom settings cleared', { type: 'success' });
        }
      } else {
        toast('Developer Settings saved successfully', { type: 'success' });
      }
    }
    
    setIsOpen(false);
    window.location.reload();
  };

  const handleAddRow = () => {
    const currentHeaders = form.getValues('customHeaders');
    const lastHeader = currentHeaders[currentHeaders.length - 1];

    if (lastHeader && (!lastHeader.key?.trim() || !lastHeader.value?.trim())) {
      // @ts-ignore
      toast('Please fill the current row before adding a new one', { type: 'warning' });
      return;
    }
    append({ key: '', value: '' });
  };

  const handleRemoveRow = (index: number) => {
    if (allHeaderFields.length > 1) {
      remove(index);
    } else {
      form.reset({ ...form.getValues(), customHeaders: [{ key: '', value: '' }] });
    }
  };

  const handleRemoveAll = () => {
    removeCustomHeaders();
    removeCustomBaseUrl();
    removeCustomEnvOverrides();
    form.reset({ 
      customBaseUrl: '',
      customHeaders: [{ key: '', value: '' }],
      customEnvOverrides: [{ key: '', value: '' }]
    });
    toast('All custom settings removed', { type: 'success' });
    setIsOpen(false);
    window.location.reload();
  };

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const handleUseDefaultGatewayUrl = () => {
    form.setValue('customBaseUrl', DEFAULT_OCEANINFRA_GATEWAY_URL, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleAddEnvOverrideRow = () => {
    const currentOverrides = form.getValues('customEnvOverrides');
    const lastOverride = currentOverrides[currentOverrides.length - 1];

    if (lastOverride && (!lastOverride.key?.trim() || !lastOverride.value?.trim())) {
      // @ts-ignore
      toast('Please fill the current row before adding a new one', { type: 'warning' });
      return;
    }
    appendEnvOverride({ key: '', value: '' });
  };

  const handleRemoveEnvOverrideRow = (index: number) => {
    if (allEnvOverrideFields.length > 1) {
      removeEnvOverride(index);
    } else {
      form.reset({ ...form.getValues(), customEnvOverrides: [{ key: '', value: '' }] });
    }
  };

  const getEnvVariableOptionsForRow = (rowIndex: number) => {
    const selectedKeysInOtherRows = allEnvOverrideFields
      .filter((_, index) => index !== rowIndex)
      .map((row) => row?.key)
      .filter(Boolean);

    const currentRowValue = allEnvOverrideFields[rowIndex]?.key;
    const filteredOptions = AVAILABLE_ENV_VARIABLE_OPTIONS.filter(
      ({ value }) => value === currentRowValue || !selectedKeysInOtherRows.includes(value)
    );

    if (
      currentRowValue &&
      !filteredOptions.some(({ value }) => value === currentRowValue)
    ) {
      return [{ label: currentRowValue, value: currentRowValue }, ...filteredOptions];
    }

    return filteredOptions;
  };

  const selectedEnvOverrideKeysCount = new Set(
    allEnvOverrideFields.map((row) => row?.key).filter(Boolean)
  ).size;
  const areAllEnvOverrideOptionsSelected = selectedEnvOverrideKeysCount >= AVAILABLE_ENV_VARIABLE_OPTIONS.length;

  return (
    <>
      <span 
        id='omniful-dev-settings' 
        onClick={handleOpenModal} 
        className='cursor-pointer bg-primary_lighter hover:bg-[#EDE9FE] rounded-full p-2 relative transition-colors flex items-center justify-center'
      >
        <Wrench size={24} className='text-primary' />
        {areCustomSettingsPresent && (
          <div className='absolute bottom-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-1 ring-white' />
        )}
      </span>

      <SideSheet
        isOpen={isOpen}
        handleModalFunc={handleClose}
        title='Developer Settings'
        size='md'
      >
        <PerfectScrollbar className="h-full">
          <form 
            className='flex flex-col h-full' 
            onSubmit={form.handleSubmit(onSubmit)} 
            onKeyDown={handleFormKeyDown}
          >
            <div className='flex flex-col gap-4 p-4'>
              <Banner
                type={BANNER_TYPES.NOTIFICATION}
                variant={BANNER_VARIANTS.WARNING}
                bannerKey={BANNER_KEYS.DEVELOPER_CORS_BANNER}
              >
                <div className='flex flex-col gap-1'>
                  <div className='text-xs'>
                    Facing CORS issues? Run this command in your terminal to open Chrome with web security disabled:
                  </div>
                  <code className='bg-orange-100 text-orange-800 px-2 py-1 rounded text-[10px] font-mono break-all'>
                    {window.navigator.platform.toLowerCase().includes('mac') 
                      ? 'open -n -a /Applications/Google\\ Chrome.app --args --user-data-dir="/tmp/chrome_dev_test" --disable-web-security'
                      : window.navigator.platform.toLowerCase().includes('win')
                        ? 'chrome.exe --user-data-dir="C:/chrome_dev_test" --disable-web-security --disable-site-isolation-trials'
                        : 'google-chrome --user-data-dir="/tmp/chrome_dev_test" --disable-web-security'
                    }
                  </code>
                </div>
              </Banner>
              {/* Connection Settings */}
              <div className='bg-white border border-primary_lighter rounded-xl p-4 shadow-sm flex flex-col gap-1'>
                <div className='flex items-center gap-2'>
                  <Wifi size={20} className='text-primary' />
                  <div className='text-base font-semibold text-dark'>Connection Settings</div>
                </div>
                <p className='text-xs text-dark_6 mb-[14px]'>
                  Override the API endpoint for this session. This is an advanced dev setting.
                </p>
                <div className='flex items-center justify-between gap-3'>
                  <div className='text-sm font-medium text-dark'>Custom Base URL</div>
                  <Button
                    type='button'
                    ofStyle={BUTTON_STYLE.NO_BACKGROUND}
                    ofType={BUTTON_TYPE.COMPRESSED}
                    onClick={handleUseDefaultGatewayUrl}
                  >
                    <Link size={14} />
                    <span className="text-xs">Use OceanInfra URL</span>
                  </Button>
                </div>
                <InputField
                  label=''
                  placeholder='e.g., http://localhost:8000'
                  {...form.register('customBaseUrl', {
                    pattern: {
                      value: REGEX_PATTERNS.URL_VALIDATION,
                      message: 'Please enter a valid URL including protocol (e.g., http://localhost:8000)'
                    }
                  })}
                  errors={form.formState.errors.customBaseUrl?.message || ''}
                />
              </div>

              {/* Request Headers */}
              <div className='bg-white border border-primary_lighter rounded-xl p-4 shadow-sm flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Server size={20} className='text-primary' />
                    <div className='text-base font-semibold text-dark'>Request Headers</div>
                  </div>
                  <Button 
                    type='button' 
                    ofStyle={BUTTON_STYLE.NO_BACKGROUND}
                    ofType={BUTTON_TYPE.COMPRESSED}
                    onClick={handleAddRow}
                  >
                    <Plus size={14} />
                    <span className="text-xs">Add New Row</span>
                  </Button>
                </div>
                <p className='text-xs text-dark_6 mb-[14px]'>
                  Inject custom commit id, feature flags, or debug headers into every outgoing request.
                </p>

                <div className='overflow-hidden'>
                  <table className='w-full border-collapse'>
                    <thead>
                      <tr className='border-b border-primary_lighter'>
                        <th className='text-left p-2 text-[11px] font-bold text-dark_6 uppercase tracking-wider w-[48%]'>Header Key</th>
                        <th className='text-left p-2 text-[11px] font-bold text-dark_6 uppercase tracking-wider w-[48%]'>Header Value</th>
                        <th className='w-[4%]'></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((field, index) => (
                        <tr key={field.id} className='border-b border-primary_lighter last:border-none'>
                          <td className='p-1 py-2'>
                            <InputField
                              label=''
                              placeholder='Key'
                              {...form.register(`customHeaders.${index}.key` as const)}
                            />
                          </td>
                          <td className='p-1 py-2'>
                            <InputField
                              label=''
                              placeholder='Value'
                              {...form.register(`customHeaders.${index}.value` as const)}
                            />
                          </td>
                          <td className='p-1 py-2 text-center'>
                            <button
                              type='button'
                              onClick={() => handleRemoveRow(index)}
                              disabled={allHeaderFields.length === 1 && (!allHeaderFields[0]?.key?.trim() && !allHeaderFields[0]?.value?.trim())}
                              className='flex items-center justify-center text-danger transition-colors bg-transparent border-none mx-auto disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <Banner
                type='NOTIFICATION'
                variant='INFO'
                text='Example: X-Gateway-CommitID : 213027c (Uses the first 7 characters of the commit hash)'
                bannerKey='custom-header-tip'
              />  

              <div className='bg-white border border-primary_lighter rounded-xl p-4 shadow-sm flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Code size={20} className='text-primary' />
                    <div className='text-base font-semibold text-dark'>Environment Overrides</div>
                  </div>
                  <Button
                    type='button'
                    ofStyle={BUTTON_STYLE.NO_BACKGROUND}
                    ofType={BUTTON_TYPE.COMPRESSED}
                    onClick={handleAddEnvOverrideRow}
                    disabled={areAllEnvOverrideOptionsSelected}
                  >
                    <Plus size={14} />
                    <span className="text-xs">Add New Row</span>
                  </Button>
                </div>
                <p className='text-xs text-dark_6 mb-[14px]'>
                  Override runtime environment settings for this browser only.
                </p>

                <div className='overflow-hidden'>
                  <table className='w-full border-collapse'>
                    <thead>
                      <tr className='border-b border-primary_lighter'>
                        <th className='text-left p-2 text-[11px] font-bold text-dark_6 uppercase tracking-wider w-[48%]'>Environment Setting</th>
                        <th className='text-left p-2 text-[11px] font-bold text-dark_6 uppercase tracking-wider w-[48%]'>Value</th>
                        <th className='w-[4%]'></th>
                      </tr>
                    </thead>
                    <tbody>
                      {envOverrideFields.map((field, index) => (
                        <tr key={field.id} className='border-b border-primary_lighter last:border-none'>
                          <td className='p-1 py-2'>
                            <Controller
                              name={`customEnvOverrides.${index}.key`}
                              control={form.control}
                              render={({ field }) => (
                                <Select
                                  variant='floatingdropdown'
                                  name={field.name}
                                  placeholder='Select environment setting'
                                  label=''
                                  options={getEnvVariableOptionsForRow(index)}
                                  value={getEnvVariableOptionsForRow(index).find(({ value }) => value === field.value) || null}
                                  onChange={(selectedOption: unknown) => {
                                    const option = selectedOption as { value?: string } | null;
                                    field.onChange(option?.value || '');
                                  }}
                                  errors={form.formState.errors as Record<string, { message: string }>}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    placeholder: (base) => ({ ...base, fontSize: '12px' }),
                                  }}
                                />
                              )}
                            />
                          </td>
                          <td className='p-1 py-2'>
                            <InputField
                              label=''
                              placeholder='e.g., your-value'
                              {...form.register(`customEnvOverrides.${index}.value` as const)}
                            />
                          </td>
                          <td className='p-1 py-2 text-center'>
                            <button
                              type='button'
                              onClick={() => handleRemoveEnvOverrideRow(index)}
                              disabled={allEnvOverrideFields.length === 1 && (!allEnvOverrideFields[0]?.key?.trim() && !allEnvOverrideFields[0]?.value?.trim())}
                              className='flex items-center justify-center text-danger transition-colors bg-transparent border-none mx-auto disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <SidesheetFooter className='flex justify-end gap-3 sticky bottom-0 mt-auto bg-white border-t p-4'>
              <Button type='button' ofStyle={BUTTON_STYLE.OUTLINED} onClick={handleRemoveAll}>
                Clear All
              </Button>
              <Button type='submit'>
                Apply Settings
              </Button>
            </SidesheetFooter>
          </form>
        </PerfectScrollbar>
      </SideSheet>
    </>
  );
};

const DeveloperSettingsWrapper = () => {
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === WORKSPACE_ENVIRONMENT.DEVELOPMENT;
  
  if (!isDevelopment) {
    return null;
  }
  
  return <DeveloperSettingsModalContent />;
};

export default DeveloperSettingsWrapper;
